<template>
  <div class="floor-plan-ticket-filter">
    <h2 class="floor-plan-ticket-filter__title">{{ label }}</h2>
    <v-select
      ref="select"
      :value="value === null ? 'all' : value"
      :items="options"
      :label="label"
      item-value="id"
      item-text="name"
      class="floor-plan-ticket-filter__select"
      :menu-props="menuProps"
      hide-details
      solo
      flat
      @focus="syncMenuGeometry"
      @click.native="syncMenuGeometry"
      @input="$emit('input', $event === 'all' ? null : $event)"
    >
      <template v-slot:selection="{ item }">
        <div class="floor-plan-ticket-filter__selection">
          <img v-if="item.id === 'all'" src="/assets/floor-plan-ticket-filter/all-ticket-types.svg" alt="">
          <span v-else class="floor-plan-ticket-filter__color" :style="{ backgroundColor: item.color }" />
          <span class="floor-plan-ticket-filter__name">{{ item.name }}</span>
        </div>
      </template>
      <template v-slot:item="{ item }">
        <div v-if="item.id === 'all'" class="floor-plan-ticket-filter__all">
          <img src="/assets/floor-plan-ticket-filter/all-ticket-types.svg" alt="">
          <span>{{ item.name }}</span>
        </div>
        <ticket-dropdown-item v-else :ticket="item" filter-style />
      </template>
      <template v-slot:append>
        <img class="floor-plan-ticket-filter__chevron" src="/assets/floor-plan-ticket-filter/chevron-down.svg" alt="">
      </template>
    </v-select>
  </div>
</template>

<script>
import TicketDropdownItem from './ticket-dropdown-item.vue'

export default {
  name: 'floor-plan-ticket-filter',
  components: { TicketDropdownItem },
  props: {
    value: { type: [Number, String], default: null },
    tickets: { type: Array, default: () => [] },
    label: { type: String, required: true },
    allLabel: { type: String, required: true },
  },
  data() {
    return { menuWidth: 251, menuMaxHeight: 520 }
  },
  computed: {
    options() {
      return [{ id: 'all', name: this.allLabel }, ...this.tickets]
    },
    menuProps() {
      return {
        contentClass: 'floor-plan-ticket-filter-menu',
        minWidth: this.menuWidth,
        maxWidth: this.menuWidth,
        maxHeight: this.menuMaxHeight,
        offsetY: true,
        transition: false,
      }
    },
  },
  mounted() {
    this.$nextTick(this.syncMenuGeometry)
    window.addEventListener('resize', this.syncMenuGeometry)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.syncMenuGeometry)
  },
  methods: {
    syncMenuGeometry() {
      if (!this.$refs.select) return
      const bounds = this.$refs.select.$el.getBoundingClientRect()
      this.menuWidth = Math.min(bounds.width, window.innerWidth - 32)
      this.menuMaxHeight = Math.min(520, Math.max(160, window.innerHeight - bounds.bottom - 16))
    },
  },
}
</script>

<style scoped>
.floor-plan-ticket-filter {
  background: #fff;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(151, 151, 151, 0.5);
  min-width: 0;
}

.floor-plan-ticket-filter__title {
  margin: 0 0 12px;
  color: #333;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.6px;
}

.floor-plan-ticket-filter__select {
  width: 251px;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
}

.floor-plan-ticket-filter__select:focus-within {
  border-color: #0277bd;
}

.floor-plan-ticket-filter__select >>> .v-input__slot {
  min-height: 54px;
  margin: 0;
  padding: 0 16px !important;
  box-shadow: none !important;
}

.floor-plan-ticket-filter__select >>> .v-select__selections {
  min-width: 0;
  padding: 0 !important;
}

.floor-plan-ticket-filter__select >>> .v-select__selections input {
  position: absolute;
  width: 0;
  padding: 0;
}

.floor-plan-ticket-filter__select >>> .v-input__append-inner {
  margin: 0 0 0 24px;
  padding: 0;
}

.floor-plan-ticket-filter__select >>> .v-label {
  display: none;
}

.floor-plan-ticket-filter__selection,
.floor-plan-ticket-filter__all {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  color: #333;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
}

.floor-plan-ticket-filter__selection {
  flex: 1;
}

.floor-plan-ticket-filter__selection img,
.floor-plan-ticket-filter__all img,
.floor-plan-ticket-filter__color {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.floor-plan-ticket-filter__color {
  border-radius: 50%;
}

.floor-plan-ticket-filter__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.floor-plan-ticket-filter__chevron {
  display: block;
  transition: transform 0.15s ease;
}

.floor-plan-ticket-filter__select.v-select--is-menu-active .floor-plan-ticket-filter__chevron {
  transform: rotate(180deg);
}

.floor-plan-ticket-filter__all {
  padding: 16px;
  width: 100%;
}

@media (max-width: 600px), (max-width: 960px) and (max-height: 500px) {
  .floor-plan-ticket-filter {
    padding: 16px;
  }

  .floor-plan-ticket-filter__select {
    width: 100%;
  }
}
</style>

<style>
.floor-plan-ticket-filter-menu {
  border-radius: 4px;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.08), 0 0 4px rgba(0, 0, 0, 0.16);
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.floor-plan-ticket-filter-menu .v-list {
  padding: 0;
}

.floor-plan-ticket-filter-menu .v-list__tile {
  height: auto;
  padding: 0;
}

.floor-plan-ticket-filter-menu .v-list__tile:hover,
.floor-plan-ticket-filter-menu .v-list__tile--highlighted,
.floor-plan-ticket-filter-menu .v-list__tile--highlighted .ticket-list-item--filter {
  background: #e6f1f8;
}
</style>
